import { heIL } from "@material-ui/core/locale"
import { createTheme, ThemeOptions } from "@material-ui/core/styles"
import createBreakpoints from "@material-ui/core/styles/createBreakpoints"
import { Overrides } from "@material-ui/core/styles/overrides"
import { MuiPickersOverrides } from "@material-ui/pickers/typings/overrides"
import boxShadows from "./boxShadows"
import colors from "./colors"

const getFontStyle = (text: string, hebStyle: string, engStyle: string) => {
  // if unicode range is U+0000-017F change the font size to 32px
  const latinChars = /[a-zA-Z0-9]/
  if (text) {
    if (latinChars.test(text)) {
      return engStyle
    } else {
      return hebStyle
    }
  }
  return engStyle
}
const getFontStyleNumber = (
  text: string,
  hebStyle: number,
  engStyle: number
) => {
  // if unicode range is U+0000-017F change the font size to 32px
  const latinChars = /[a-zA-Z0-9]/
  if (text) {
    if (latinChars.test(text)) {
      return engStyle
    } else {
      return hebStyle
    }
  }
  return engStyle
}
const breakpoints = createBreakpoints({})

const muiThemeOptions: ThemeOptions = {
  direction: "rtl",
  spacing: 3,
  // ///////////////////////////////////////////////////////:
  // typography:
  typography: {
    fontFamily: "MazdaType, OENaPro, sans-serif;",
    h1: {
      fontSize: "28px",
      fontWeight: 500,
      lineHeight: "140%",
      color: colors.black,
      letterSpacing: "0.09em",
    },
    h2: {
      fontSize: "24px",
      fontWeight: 400,
      lineHeight: "140%",
      color: colors.black,
      letterSpacing: "0.05em",
    },
    h3: {
      fontSize: "20px",
      fontWeight: 300,
      lineHeight: "160%",
      color: colors.black,
      letterSpacing: "0.03em",
    },
    subtitle1: {
      fontSize: "20px",
      fontWeight: 300,
      lineHeight: "160%",
      color: colors.black,
      letterSpacing: "0.03em",
    },
    body1: {
      // body1 text
      fontSize: "18px",
      fontWeight: 300,
      lineHeight: "150%",
      letterSpacing: "0",
      color: colors.black,
    },
    body2: {
      // body2 text
      fontSize: "16px",
      fontWeight: 300,
      lineHeight: "140%",
      letterSpacing: "0",
      color: colors.black,
    },
    caption: {
      fontSize: "12px",
      fontWeight: 300,
      lineHeight: "130%",
      letterSpacing: "0",
      color: colors.black,
    },
    button: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "140%",
      letterSpacing: "0.05em",
      color: colors.black,
    },
  },
  // ///////////////////////////////////////////////////////:
  // colors:
  palette: {
    background: {
      default: colors.white,
    },
    primary: {
      main: colors.black,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.white,
      contrastText: colors.black,
    },
    text: {
      primary: colors.black,
      // secondary: colors.orange,
    },
    error: {
      main: "#b50303",
    },
  },
  props: {
    MuiContainer: {
      maxWidth: "lg",
    },
    MuiFormControl: {
      fullWidth: true,
    },
    MuiTextField: {
      fullWidth: true,
      variant: "outlined",
      autoComplete: "true",
    },
    // MuiOutlinedInput: {
    //   notched: true,
    // },

    MuiButton: {
      disableTouchRipple: true,
    },
  },
}

const overrides: Overrides | MuiPickersOverrides = {
  // ///////////////////////////////////////////////////////:
  // tabs:
  // MuiTabs: {
  //   root: {
  //     borderBottom: `1px solid ${colors.wheat}`,
  //   },
  //   indicator: {
  //     backgroundColor: colors.mazdaRed,
  //   },
  //   scroller: {
  //     backgroundColor: colors.white,
  //     color: colors.black,
  //   },
  //   fixed: {
  //     [breakpoints.up("md")]: {
  //       paddingTop: "50px",
  //     },
  //   },
  // },
  // MuiTab: {
  //   root: {
  //     width: "max-content",
  //     fontSize: "14px",
  //     fontWeight: 400,
  //     padding: "9px 4px 0px",
  //   },
  //   textColorPrimary: {
  //     color: colors.marbleGrey,
  //     "&.Mui-selected": {
  //       color: colors.mazdaRed,
  //     },
  //   },
  //   wrapper: {},
  // },
  MuiTypography: {
    h1: {
      letterSpacing: (props: { children: string }) => {
        return getFontStyle(props.children, "0.09em", "0.11em")
      },
      fontWeight: (props: { children: string }) => {
        return getFontStyleNumber(props.children, 400, 500)
      },
    },
    h2: {
      letterSpacing: (props: { children: string }) => {
        return getFontStyle(props.children, "0.05em", "0.06em")
      },
    },
    h3: {
      letterSpacing: (props: { children: string }) => {
        return getFontStyle(props.children, "0.03em", "0.08em")
      },
      fontWeight: (props: { children: string }) => {
        return getFontStyleNumber(props.children, 300, 400)
      },
    },
    subtitle1: {
      letterSpacing: (props: { children: string }) => {
        return getFontStyle(props.children, "0.03em", "0.08em")
      },
      fontWeight: (props: { children: string }) => {
        return getFontStyleNumber(props.children, 300, 400)
      },
    },
    body1: {
      fontWeight: (props: { children: string }) => {
        return getFontStyleNumber(props.children, 300, 400)
      },
    },
    body2: {
      fontWeight: (props: { children: string }) => {
        return getFontStyleNumber(props.children, 300, 400)
      },
    },
  },

  // ///////////////////////////////////////////////////////:
  // paper:
  MuiPaper: {
    elevation1: {
      width: "fit-content",
      backgroundColor: colors.white,
      boxShadow: boxShadows.cardShadow,
      borderRadius: "3px",
      padding: "20px",
      overflow: "hidden",
    },
    elevation4: {
      boxShadow: "none",
    },
    rounded: {
      backgroundColor: "transparent",
    },
  },
  // ///////////////////////////////////////////////////////:
  // drawer:
  MuiDrawer: {
    paper: {
      paddingTop: "56px",
    },
  },
  MuiDivider: {
    root: {
      height: "2px",
      width: "calc(100% - 60px)",
      margin: "0 auto",
    },
  },
  MuiListItem: {
    root: {
      "&:focus-visible": {
        border: "none",
        outline: "none",
      },
    },
    gutters: {
      paddingLeft: "27px",
      paddingRight: "0",
    },
  },
  MuiContainer: {
    root: {
      paddingRight: "15px",
      paddingLeft: "15px",
      [breakpoints.up("sm")]: {
        paddingRight: "15px",
        paddingLeft: "15px",
      },
    },
  },
  // ///////////////////////////////////////////////////////:
  // popup and backdrop:
  MuiBackdrop: {
    root: {
      backgroundColor: "rgba(16, 16, 16, 0.4);",
      webkitBackdropFilter: "blur(5.5px)",
      backdropFilter: "blur(5.5px)",
    },
  },
  MuiDialog: {
    root: {
      "&.continue-purchase-root": {
        padding: "10px 15px !important",

        [breakpoints.down("sm")]: {
          padding: "10px !important",
        },
      },
    },
    paperWidthSm: {
      borderRadius: 30,
      overflow: "hidden",
      [breakpoints.up("md")]: {
        flexDirection: "row",
        maxHeight: "100%",
      },
    },
    paperWidthXs: {
      maxWidth: "238px",

      "&.continue-purchase": {
        width: "340px",
        maxWidth: "340px",
      },
    },
    paperScrollPaper: {
      maxHeight: "calc(100% - 90px)",
      overflowY: "initial",
    },
    paper: {
      width: "100%",
      margin: "32px 6px;",
    },
    paperScrollBody: {
      overflow: "visible",
      margin: "50px 6px;",

      [breakpoints.down("md")]: {
        width: "calc(100% - 12px)",
        maxWidth: "100% !important",
      },
    },
  },
  MuiDialogContent: {
    root: {
      borderRadius: "5px",
      // boxShadow: "0 5px 99px 0 rgba(9, 42, 70, 0.6)",
      // border: "solid 1px #707070",
      backgroundColor: colors.white,
    },
  },
  MuiSvgIcon: {
    colorPrimary: {
      color: colors.black,
    },
  },
  // ///////////////////////////////////////////////////////:
  // buttons:
  MuiButton: {
    // this is the classic button:
    text: {
      minWidth: "120px",
      fontSize: "16px",
      width: "fit-content",
      color: "#FFFFFF",
      backgroundColor: "#101010",
      border: `2px solid #101010`,
      padding: "11.5px 13px",
      "&:hover": {
        boxShadow: "none",
        backgroundColor: "#3A3D46",
        color: "#FFFFFF",
        border: `2px solid #3A3D46`,
      },
    },
    textSecondary: {
      minWidth: "120px",
      fontSize: "16px",
      width: "fit-content",
      color: "#101010",
      backgroundColor: "#FFFFFF",
      border: `2px solid #FFFFFF`,
      padding: "11.5px 13px",
      "&:hover": {
        boxShadow: "none",
        backgroundColor: "#D5D5D5",
        color: "#101010",
        border: `2px solid #D5D5D5`,
      },
    },
    outlined: {
      minWidth: "120px",
      fontSize: "16px",
      width: "fit-content",
      color: "#101010",
      backgroundColor: "transparent",
      border: `2px solid #101010`,
      padding: "11.5px 13px",
      "&:hover": {
        boxShadow: "none",
        backgroundColor: "#3A3D46",
        color: "#FFFFFF",
        border: `2px solid #3A3D46`,
      },
    },
    outlinedSecondary: {
      minWidth: "120px",
      fontSize: "16px",
      width: "fit-content",
      color: "#FFFFFF",
      backgroundColor: "transparent",
      border: `2px solid #FFFFFF`,
      padding: "11.5px 13px",
      "&:hover": {
        boxShadow: "none",
        backgroundColor: "#D5D5D5 !important",
        color: "#101010",
        border: `2px solid #D5D5D5`,
      },
    },
    contained: {
      // this is the article button with the undlerline:
      minWidth: "0px",
      fontSize: "16px",
      width: "fit-content",
      color: "#101010",
      backgroundColor: "transparent",
      border: "transparent",
      borderRadius: "0",
      borderBottom: "2px solid #101010",
      padding: "0px",
      margin: "4px 0px",
      boxShadow: "none",
      "&:hover": {
        boxShadow: "none",
        backgroundColor: "transparent",
        border: "transparent",
        color: "#910A2D",
        borderBottom: "2px solid #910A2D",
      },
      "&:disabled": {
        boxShadow: "none",
        backgroundColor: "transparent",
        border: "transparent",
        borderRadius: "0",
        color: "#616161",
        borderBottom: "2px solid #910A2D",
      },
      "&:focus": {
        boxShadow: "none",
        backgroundColor: "transparent",
        border: "transparent",
        color: "#910A2D",
        borderBottom: "2px solid #910A2D",
      },
    },
    containedSecondary: {
      minWidth: "0px",
    },

    sizeSmall: {
      padding: "11.5px 13px",
      fontSize: "16px",
      fontWeight: 400,
      minHeight: "0px",
      minWidth: "0px",
      height: "fit-content",
    },
    root: {
      minWidth: "120px",
      fontSize: "16px",
      width: "fit-content",
      padding: "11.5px 13px",
      "&:hover": {
        boxShadow: "none",
        backgroundColor: "#3A3D46",
        color: "#FFFFFF",
        border: `2px solid #3A3D46`,
      },
      "&:disabled": {
        boxShadow: "none",
        backgroundColor: "#999999",
        color: "#FFFFFF",
        border: `2px solid #999999`,
      },
      "&:focus-visible": {
        border: "2px dashed white",
        outlineOffset: "2px",
        outline: "3px dashed #0e3487",
      },
    },
    label: {
      textAlign: "center",
    },
  },
  // ///////////////////////////////////////////////////////:
  // Accordion:
  MuiAccordion: {
    root: {
      border: "1.5px solid white",
      minHeight: "0px",
      boxShadow: "none",
      "&:not(:last-child)": {
        borderBottom: 0,
      },
      "&:before": {
        display: "none",
      },
      "&$expanded": {
        margin: "auto",
      },
    },
    rounded: {
      "&:last-child": {
        borderBottomRightRadius: "16px",
        borderBottomLeftRadius: "16px",
        overflow: "hidden",
      },
      "&:first-child": {
        borderTopRightRadius: "16px",
        borderTopLeftRadius: "16px",
        overflow: "hidden",
      },
    },
    expanded: {},
  },
  MuiAccordionDetails: {
    root: {
      flexWrap: "wrap",
      padding: "8px 15px",
      // [breakpoints.up("md")]: {
      //   padding: "8px 15px",
      // },
    },
  },
  MuiAccordionSummary: {
    root: {
      minHeight: "0px",
      backgroundColor: colors.flour,
      padding: "0px",
      "&$expanded": {
        minHeight: "0px",
      },
    },
    content: {
      margin: "0px",
      padding: "14px 15px",
      alignItems: "center",
      "&$expanded": {
        margin: "0px",
        padding: "14px 15px",
        alignItems: "center",
      },
    },
    expandIcon: {
      "&$expanded": {
        transform: "scaleY(-1)",
      },
    },
  },
  // ///////////////////////////////////////////////////////:
  // forms elements:
  // datepicker:
  // MuiPopover: { paper: { backgroundColor: colors.white } },
  MuiFormControl: {
    root: {
      borderRadius: "16px",
    },
  },
  // regular input:
  MuiInput: {
    input: {
      borderRadius: 3,
      height: "45px",
      boxSizing: "border-box",
      "&::placeholder": {
        textOverflow: "ellipsis !important",
        // color: "blue",
        fontSize: 16,
      },
    },
    root: {
      "&$disabled": {
        color: colors.flour,
      },
    },
  },
  MuiOutlinedInput: {
    input: {
      borderColor: "#999999",
      borderWidth: 0.5,
      padding: "11px 10px",
      "&::placeholder": {
        textOverflow: "ellipsis !important",
        color: colors.marbleGrey,
        fontSize: 16,
      },
    },

    root: {
      borderRadius: 3,
      backgroundColor: colors.white,
      color: colors.black,
      fontSize: 16,
      borderColor: colors.darkGrey,
      borderWidth: 0.5,
      "& legend": {
        // the small space in mui input
        width: "0px",
        fontWeight: "normal",
        fontSize: "16px",
      },
      "&::placeholder": { color: colors.marbleGrey, fontSize: 16 },
      "& $notchedOutline": {
        borderColor: colors.darkGrey,
        borderWidth: 0.5,
      },
      "&:hover $notchedOutline": {
        // borderColor: colors.transparent,
        borderWidth: 0.5,
      },

      "&$focused $notchedOutline": {
        // borderColor: colors.transparent,
        borderWidth: 0.5,

        // boxShadow: "0px 0px 0px 3.2px rgba(69, 37, 28, 0.25)",
      },
      "&$error $notchedOutline": {
        // borderColor: colors.transparent,
        // boxShadow: "0px 0px 0px 3.2px rgba(171, 78, 78, 0.25)",
        borderWidth: 0.5,
        color: colors.black,
      },
      "&$error": {
        backgroundColor: colors.white,
        boxShadow: "none",
        borderWidth: 0.5,
      },
      "&$focused": {
        backgroundColor: colors.white,
        boxShadow: "none",
        borderWidth: 0.5,
      },
    },
  },
  // autocomplete:
  MuiAutocomplete: {
    inputRoot: {
      fullWidth: true,
      // the autocomplete wrapper
      width: "100%",
      paddingTop: "0px !important",
      paddingBottom: "0px !important",
      '&&[class*="MuiOutlinedInput-root"] $input': {
        width: "100%",
        padding: "11px 10px",
        fullWidth: true,
      },
    },
    root: {
      width: "100%",
    },
  },
  MuiInputAdornment: {
    root: {
      color: colors.black,
    },
  },
  // forms labels:
  MuiInputLabel: {
    root: {
      backgroundColor: "transparent",
      direction: "rtl",
      color: colors.disable,
    },
    outlined: {
      transform: "translate(10px, 13px) scale(1)",
      legend: {
        fontWeight: "bold",
        fontSize: "16px",
      },
      "&$shrink": {
        // the lable when the input is focused
        transform: "translate(0px, -17px) scale(0.75)",
        color: colors.disable,
      },
    },
  },
  // autocomplete:

  MuiFormLabel: {
    root: {
      flexBasis: "100%",
      direction: "rtl",
      color: colors.disable,
      fontWeight: "normal",
      fontSize: "16px",
      lineHeight: 1.14,
      // sdkfhdsjfhksdfh
      "&$focused": {
        color: colors.disable,
        "& .MuiFormLabel-asterisk": {
          color: colors.mazdaRed,
        },
      },

      "&$error": {
        color: colors.disable,
      },
    },
    asterisk: {
      marginLeft: "1px",
      verticalAlign: "top",
      color: colors.mazdaRed,
    },
  },

  MuiFormControlLabel: {
    root: {
      alignItems: "flex-start",
      ".MuiFormGroup-root &": {
        display: "inline-block",
        marginRight: "0",
        marginLeft: "0",
        "& .MuiButtonBase-root": {
          display: "none",
        },
      },
    },
  },
  MuiButtonBase: {
    root: {
      "&:focus-visible": {
        border: "2px dashed white",
        outline: "3px dashed #0e3487",
      },
    },
  },

  MuiSwitch: {
    root: {
      width: 28,
      height: 16,
      padding: 0,
      display: "flex",
    },
    switchBase: {
      padding: 2,
      color: colors.sand,
      "&$checked": {
        transform: "translateX(12px)",
      },
    },
    thumb: {
      width: 12,
      height: 12,
      boxShadow: "none",
    },
    colorSecondary: {
      "&$checked": {
        // Controls checked color for the thumb
        color: colors.white,
      },
    },
    track: {
      border: `1px solid ${colors.sand}`,
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: colors.white,
      "$checked$checked + &": {
        // Controls checked color for the track
        border: `1px solid ${colors.mazdaRed}`,
        opacity: 1,
        backgroundColor: colors.mazdaRed,
      },
    },
  },
  // checkbox
  MuiCheckbox: {
    colorSecondary: {
      color: colors.sand,
      "&$checked": {
        color: "rgba(177, 17, 22, 0.8);",
      },
    },
  },

  // radio:
  MuiRadio: {
    colorSecondary: {
      // backgroundColor: colors.sand,
      color: colors.sand,
      "&$checked": {
        color: "rgba(177, 17, 22, 0.8);",
        // "& input + *": {
        //   backgroundColor: colors.sand,
        // },
      },
    },
  },
  // dropdown (select):
  MuiMenu: {
    paper: {
      background: colors.white,
    },
  },
  // dropdown item (list:)
  MuiMenuItem: {
    root: {
      paddingTop: 0,
      paddingBottom: 0,
      background: "white",
      "&": {
        fontSize: 15,
        paddingTop: 4,
        paddingBottom: 4,
        paddingRight: 16,
        paddingLeft: 16,
        minHeight: "25px;",
      },
      "&:hover": {
        background: "rgba(247, 243, 235, 0.4);",
      },
      "&.Mui-selected": {
        background: colors.flour,
      },
      "&.Mui-selected:hover": {
        background: "rgba(247, 243, 235, 0.4);",
      },
    },
  },
  MuiFormHelperText: {
    root: {
      fontSize: 12,
      marginTop: "3px",
      marginRight: "0px !important",
      marginLeft: "0px !important",
      letterSpacing: "0.4px",
      "&$error": {
        color: colors.mazdaRed,
        textAlign: "start",
      },
    },
  },
  // ///////////////////////////////////////////////////////:
  // datepicker:

  // MuiPickersDatePickerRoot: {
  //   toolbar: {
  //     backgroundColor: colors.mazdaRed,
  //     flexDirection: "row-reverse",
  //     alignItems: "center",
  //     justifyContent: "space-between",
  //     height: "auto",
  //     paddingLeft: "16px",
  //     paddingRight: "16px",
  //   },
  // },
  // MuiPickersDay: {
  //   day: {
  //     color: colors.darkGrey,
  //     "&:hover": {
  //       backgroundColor: "rgba(247, 243, 235, 0.8);",
  //     },
  //   },
  //   daySelected: {
  //     color: colors.white,
  //     backgroundColor: "rgba(177, 17, 22, 0.8);",
  //     "&:hover": {
  //       backgroundColor: colors.mazdaRed,
  //     },
  //   },
  //   dayDisabled: {
  //     color: colors.flour,
  //   },
  //   current: {
  //     color: colors.mazdaRed,
  //   },
  // },
  // end of datepickers
  MuiAvatar: {
    root: {
      width: 24,
      height: 24,
      marginBottom: "0 !important",
    },
    img: {
      objectFit: "contain",
    },
  },
}
muiThemeOptions.overrides = overrides

const theme = {
  ...createTheme(muiThemeOptions, heIL),
  colors,
  boxShadows,
}

export default theme

export type Theme = typeof theme
